import * as React from 'react'
import { DataGrid } from '@mui/x-data-grid'
import {
  Box,
  Button,
  Chip,
  Link,
  MenuItem,
  Select,
  Skeleton,
  Typography,
} from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { BackArrowIcon, HighDemandIcon, TopRatingIcon, TrendingIcon } from '../icons'
import { GlobalStyles } from '@mui/material'
import ThreeDotMenu from '../components/university-table/ThreeDotMenu'
import UniversitySummary from '../components/university-table/UniversitySummary'
import { fetchSpecializationList, fetchSpecializationNameList, fetchSpecializationsNames, fetchSpecializationsNameSuggestion } from '../api/specializationApi'
import { fetchUniversityDashboardStats } from '../api/universityApi'
import { defaultPaginationValues, filterTypeOptions, tagOptions } from '../utils/constant'
import { updateTag } from '../api/tagApi'
import { updateStatus } from '../api/adminApi'
import CommonFilterTab from '../components/common/CommonFilterTab'
import { useSelector } from 'react-redux'
import { useDispatch } from 'react-redux'
import CommonDataTable from '../components/common/CommonDataTable'
import { setFilterField } from '../containers/filter-container/reducer/filterSlice'
import { formatMultiQueryParams, jsonParserFormatter } from '../utils/helperFunction'

const SpecializationListingManagement = ({ isHandlBtn = false }) => {
  const filterSlice = useSelector(state => state.filter?.filters)
  const LOCATION_FILTER = `filters:${window.location.pathname}`
  const dispatch = useDispatch()
  const [rows, setRows] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const navigate = useNavigate()
  const [selectedStatus, setSelectedStatus] = React.useState('')
  const [rowCount, setRowCount] = React.useState(0);
  const [searchFilter, setSearchFilter] = React.useState({})
  const [pageFilters, setPageFilters] =  React.useState(defaultPaginationValues);
  const [isRender,setIsRender] = React.useState(false)
  const [specializationSuggestion,setSpecializationSuggestion] = React.useState([])
  
  

  const handleFliterSliceChange = (values = {}, isReset = false) => {
    let isQuery = typeof values === "object" && Object.keys(values)?.length
    isQuery && dispatch(setFilterField({ pathname: LOCATION_FILTER, filters: JSON.stringify({ ...(isReset ? {} : pageFilters), ...defaultPaginationValues, ...values }) }))
  }

  React.useEffect(() => {
    setLoading(true)
    const multiFilter = formatMultiQueryParams(filterSlice[LOCATION_FILTER])
    fetchSpecializationNameList(setRows,{ ...searchFilter, ...multiFilter, isMultiFilter: true }, false, setRowCount)
      .finally(() => setLoading(false))
  }, [filterSlice[LOCATION_FILTER], isRender])

  // handle extra filter tabs changes
  React.useEffect(() => {
    if (pageFilters?.page) {
      handleFliterSliceChange({ page: 0 })
    } else {
      setIsRender(!isRender)
    }
  }, [searchFilter])

  // parse page filters
  React.useEffect(() => {
    const values = jsonParserFormatter(filterSlice[LOCATION_FILTER])
    setPageFilters(values)
  }, [filterSlice[LOCATION_FILTER]])

  React.useEffect(() => {
    fetchSpecializationsNameSuggestion((result) => { setSpecializationSuggestion(result?.map(el => ({...el, value: el?.label }))) }, { skipLimit: true })
      .catch(() => { })
  }, [])

  const handleChange = async (id, field, value) => {
    if (field === 'action') {
      if (value === 'View') {
        return;
      }

      if (value === 'Send to draft') {
        const prevStatus = rows.find(r => r.id === id)?.status || '';
        setRows(prev => prev.map(r => r.id === id ? { ...r, status: 'Draft' } : r));
        try {
          await updateStatus({
            id,
            status: 'draft',
            type: 'university',
          });
        } catch (err) {
          setRows(prev => prev.map(r => r.id === id ? { ...r, status: prevStatus } : r));
        }
        return;
      }
      return;
    }
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    )
    if (field === 'tag') {
      try {
        await updateTag({
          id,
          tag: value,
          type: 'specializationName',
        })
      } catch (err) {
        setRows((prev) =>
          prev.map((row) =>
            row.id === id ? { ...row, [field]: prev.find(r => r.id === id)?.tag || '' } : row
          )
        )
      }
    }
  }

  const columns = [
    {
      field: 'sNo',
      headerName: 'S. No.',
      width: 60,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      // renderCell: (params) => rows?.findIndex((r) => r.id === params.row.id) + 1,
    },
    // Removed universityId field as per your comment
    {
      field: 'name',
      headerName: 'Specialization Name',
      flex: 2,
      minWidth: 280,
      filterColumnName:"name",
      filterType:filterTypeOptions.selection,
      selection:specializationSuggestion,
      renderCell: (params) => {
        const { logo, name, id } = params.row

        const getInitials = (fullName) => {
          if (!fullName) return ''
          const words = fullName.trim().split(' ').filter(Boolean)
          if (words.length === 1) return words[0][0]?.toUpperCase() || ''
          if (words.length === 2) return (words[0][0] + words[1][0]).toUpperCase()
          return (words[0][0] + words[1][0] + words[2][0]).toUpperCase()
        }

        const stringToColor = (str) => {
          let hash = 0
          for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash)
          }
          const c = (hash & 0x00ffffff).toString(16).toUpperCase()
          return '#' + '00000'.substring(0, 6 - c.length) + c
        }

        const bgColor = stringToColor(name || 'Course')

        return loading ? (
          <Skeleton width={150} />
        ) : (
          <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{ cursor: 'pointer' }}
            onClick={() => navigate(`/specialization-listing-management/parents-specialization/${id}`)}
          >
            {logo ? (
              <Box
                component="img"
                src={logo}
                alt="logo"
                sx={{
                  width: 40,
                  height: 40,
                  padding: '5px',
                  borderRadius: '50%',
                  objectFit: 'contain',
                  backgroundColor: '#fff',
                  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
                  display: 'block',
                  maxHeight: 60,
                }}
              />
            ) : (
              <Box
                sx={{
                  minWidth: 35,
                  minHeight: 35,
                  maxWidth: 35,
                  maxHeight: 35,
                  borderRadius: '50%',
                  backgroundColor: bgColor,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 500,
                  fontSize: '10px',
                  textTransform: 'uppercase',
                  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.15)',
                }}
              >
                {getInitials(name)}
              </Box>
            )}
            {params.value}
          </Box>
        )
      },
    },
    {
      field: 'specializationCount',
      headerName: 'Specialization Variant',
      flex: 1.5,
      minWidth: 230,
      filterColumnName:"specializationCount",
      filterType:filterTypeOptions.numberRange,
      renderCell: (params) =>
        loading ? (
          <Skeleton width={40} />
        ) : (
          <Chip 
           onClick={() => navigate(`/specialization-listing-management/parents-specialization/${params.row?.id}`)}
          label={params.value} 
          sx={{ background: '#DFEDFF', color: '#363853' }} />
        ),
    },
    {
      field: 'universityCount',
      headerName: 'University',
      flex: 1,
      minWidth: 130,
      filterColumnName:"universityCount",
      filterType:filterTypeOptions.numberRange,
      renderCell: (params) =>
        loading ? (
          <Skeleton variant="circular" width={24} height={24} />
        ) : (
          <Chip onClick={()=>{}} label={params.value} sx={{ cursor:'default',background: '#DFEDFF', color: '#363853' }} />
        ),
    },
    {
      field: 'tag',
      headerName: 'Tag',
      flex: 1,
      minWidth: 180,
      filterColumnName:"tag",
      filterType:filterTypeOptions.selection,
      selection:tagOptions,
      renderCell: (params) =>
        loading ? (
          <Skeleton width={100} />
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', height: '100%' }}>
            <Select
              value={params.value}
              onChange={(e) => handleChange(params.row.id, 'tag', e.target.value)}
              size="small"
              disabled={selectedStatus === 'Deactivated'}
              fullWidth
              IconComponent={KeyboardArrowDownIcon}
              sx={{
                borderRadius: '50px',
                fontSize: '13px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderRadius: '50px',
                },
                '& .MuiSelect-select': {
                  paddingY: '8px',
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
            >
              {tagOptions.map((tag) => (
                <MenuItem key={tag.value} value={tag.value}>
                  <Box display="flex" alignItems="center" gap={1} sx={{ fontSize: '13px' }}>
                    {tag.icon}
                    {tag.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </Box>
        ),
    },
  ]


  return (
    <>
      {/* Apply global styles to DataGrid dropdown menus */}
      {/* <GlobalStyles
        styles={{
          '.MuiDataGrid-menuList': {
            fontSize: '13px',
            padding: '4px 0',
          },
          '.MuiDataGrid-panelContent': {
            fontSize: '13px',
            padding: '4px 0',
          },
          '.MuiSelect-select': {
            fontSize: '13px',
            padding: '4px 0',
          },
          '.MuiList-root .MuiButtonBase-root': {
            fontSize: '13px',
          },
          '.MuiDataGrid-panelContent .MuiOutlinedInput-input': {
            fontSize: '13px',
          },
          '.MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiFormControlLabel-label': {
            fontSize: '13px',
          },
          '.MuiDataGrid-menuList .MuiTypography-root': {
            fontSize: '13px',
          },
          '.MuiDataGrid-menuList .MuiMenuItem-root': {
            fontSize: '13px',
            padding: '6px 12px',
          },
          '.MuiDataGrid-menuList .MuiMenuItem-root:hover': {
            backgroundColor: '#f5f5f5',
          },
          '& .MuiDataGrid-columnHeaders': {
            position: 'sticky',
            top: 0,
            zIndex: 2,
            backgroundColor: '#fff',
          },
          '& .MuiDataGrid-cell.sticky-col-1': {
            position: 'sticky',
            left: 0,
            background: '#fff',
            zIndex: 2,
          },
          '& .MuiDataGrid-columnHeader.sticky-col-1': {
            position: 'sticky',
            left: 0,
            background: '#fff',
            zIndex: 3, // slightly higher than cell
          },
        }}
      /> */}

      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div className="page-header" style={{ position: 'sticky', top: '4.5rem', zIndex: '9', padding: '10px 15px', borderBottom: '1px solid #E6E7E8', background: '#fff', }}>
          <div className="row align-items-center">
            <div className="col">
              <Typography sx={{ fontSize: '18px', fontWeight: '600', color: '#15243E', display: 'flex', alignItems: 'center', gap: 1, }}><Link href="/" className='d-flex'><BackArrowIcon /></Link>Specialization Listing Management </Typography>
            </div>
            {/* End Col */}
            {isHandlBtn && (<div className="col">  <Box display="flex" gap={2} sx={{ justifyContent: 'end', }}>

              <Button sx={{ border: '1px solid #0463EF', color: '#0463EF', background: '#fff', minHeight: 'auto', padding: '4px 15px', fontSize: '14px', textTransform: 'capitalize', }}> Add Specialization Titles
              </Button>
              <Button onClick={() => navigate('/specialization-listing-management/specialization-details')} sx={{ background: '#0463EF', color: '#fff', minHeight: 'auto', padding: '4px 15px', fontSize: '14px', textTransform: 'capitalize', }}>
                Add New Specialization
              </Button>
            </Box></div>)}
          </div>
          {/* End Col */}
        </div>
        <UniversitySummary type="specialization" isAdmin />
        {/* Filter Toolbar */}
        <CommonFilterTab type='specialization' searchPlaceholder='Search specialization name...' detailType='Specialization Details' fetchList={setSearchFilter} showCount={rows?.length} rowCount={rowCount} />


        <CommonDataTable columns={columns} rows={rows} idKey="field" loading={loading} filterEnabled={true} rowCount={rowCount} 
        pageFilters={pageFilters}
        handleFliterSliceChange={handleFliterSliceChange}
         />

      </Box>
    </>
  )
}

export default SpecializationListingManagement
